import { decodeFixedBase64Url } from "./base64url";
import {
  LINK_CAPABILITY_BYTES,
  LINK_KEY_BYTES,
  LINK_PROTOCOL_VERSION,
} from "./constants";

export interface DownloadLinkCapability {
  accessToken: string;
  decryptionKey: string;
  version: typeof LINK_PROTOCOL_VERSION;
}

export function validateAccessToken(value: string): void {
  decodeFixedBase64Url(value, LINK_CAPABILITY_BYTES, "Access token");
}

export function validateRevokeToken(value: string): void {
  decodeFixedBase64Url(value, LINK_CAPABILITY_BYTES, "Revoke token");
}

export function validateDecryptionKey(value: string): void {
  decodeFixedBase64Url(value, LINK_KEY_BYTES, "Decryption key");
}

export function buildDownloadLink(
  baseUrl: string | URL,
  capability: Omit<DownloadLinkCapability, "version">,
): string {
  validateAccessToken(capability.accessToken);
  validateDecryptionKey(capability.decryptionKey);

  const url = new URL(baseUrl);
  url.search = "";
  url.hash = `/download?t=${capability.accessToken}&k=${capability.decryptionKey}&v=${String(LINK_PROTOCOL_VERSION)}`;
  return url.toString();
}

export function parseDownloadFragment(hash: string): DownloadLinkCapability {
  const prefix = "#/download?";
  if (!hash.startsWith(prefix)) {
    throw new Error("Invalid download link.");
  }

  const parameters = new URLSearchParams(hash.slice(prefix.length));
  const expectedNames = new Set(["t", "k", "v"]);
  const actualNames = [...parameters.keys()];
  if (
    actualNames.length !== expectedNames.size ||
    actualNames.some((name) => !expectedNames.has(name)) ||
    [...expectedNames].some((name) => parameters.getAll(name).length !== 1)
  ) {
    throw new Error("Invalid download link.");
  }

  const accessToken = parameters.get("t");
  const decryptionKey = parameters.get("k");
  if (
    accessToken === null ||
    decryptionKey === null ||
    parameters.get("v") !== String(LINK_PROTOCOL_VERSION)
  ) {
    throw new Error("Unsupported or incomplete download link.");
  }

  validateAccessToken(accessToken);
  validateDecryptionKey(decryptionKey);

  return {
    accessToken,
    decryptionKey,
    version: LINK_PROTOCOL_VERSION,
  };
}
